<template>
  <b-card no-body v-if="ready" class="card-container">
    <!-- HEADER : barre de recherche, filtres et bouton d’upload -->
    <template #header>
      <b-row class="mb-2 mt-2">
        <!-- Colonne gauche : recherche + filtres -->
        <b-col md="6">
          <!-- Barre de recherche -->
          <b-input-group class="search-widget">
            <template #prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search" />
              </b-input-group-text>
            </template>

            <b-form-input
              type="search"
              :placeholder="$t('search_imports')"
              v-model="filters.query"
            ></b-form-input>

            <template #append>
              <b-input-group-append>
                <b-button
                  variant="outline-secondary"
                  @click="showFilters = !showFilters"
                >
                  <font-awesome-icon icon="sliders-h" />
                  {{ $t("advanced_search") }}
                </b-button>
              </b-input-group-append>
            </template>
          </b-input-group>

          <!-- Filtres avancés (exemple : has_errors, is_processed) -->
          <div
            class="reports-filters card p-3 bg-gradient-light mt-3"
            v-show="showFilters"
          >
            <b-row>
              <b-col>
                <b-form-select
                  v-model="filters.hasErrors"
                  class="mb-3"
                  :options="hasErrorsOptions"
                  :value="null"
                >
                  <template #first>
                    <option :value="null">{{ $t("has_errors") }}</option>
                  </template>
                </b-form-select>

                <b-form-select
                  v-model="filters.isProcessed"
                  class="mb-2 mr-sm-2 mb-sm-0"
                  :options="isProcessedOptions"
                  :value="null"
                >
                  <template #first>
                    <option :value="null">{{ $t("is_processed") }}</option>
                  </template>
                </b-form-select>
              </b-col>
            </b-row>
          </div>
        </b-col>

        <!-- Colonne droite : bouton d’upload (ou création) -->
        <b-col md="6" class="text-right">
          <b-button
            v-if="can('manage orders imports')"
            variant="primary"
            class="btn-round"
            @click="showUploadModal = true"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
            {{ $t("upload_orders") }}
          </b-button>
        </b-col>
      </b-row>
    </template>

    <!-- BODY : tableau + pagination -->
    <b-card-body>
      <!-- Tableau listant les orders importés -->
      <b-table
        striped
        hover
        responsive
        id="import-orders-table"
        ref="importOrdersTable"
        :fields="fields"
        :items="importOrders"
        :current-page="pagination.currentPage"
        :busy="loading"
        show-empty
        :empty-text="$t('no_import_found')"
      >
        <!-- Slot “empty” pour personnaliser le message quand la liste est vide -->
        <template #empty="scope">
          <div class="lead text-center py-3">
            {{ scope.emptyText }}
          </div>
        </template>

        <!-- Slot affiché lorsque la table est “busy” -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" />
            <strong>{{ $t("loading") }}</strong>
          </div>
        </template>

        <!-- Personnalisation des colonnes (exemples) -->
        <template #cell(errors)="data">
          {{ data.item.errors && data.item.errors.length ? "Yes" : "No" }}
        </template>

        <template #cell(is_processed)="data">
          {{ data.item.is_processed ? "Yes" : "No" }}
        </template>

        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <template #cell(actions)="data">
          <div class="text-nowrap">
            <!-- Bouton “Voir” -->
            <b-button
              size="sm"
              variant="outline-primary"
              :href="viewRoute(data.item.id)"
            >
              <font-awesome-icon :icon="['fas', 'eye']" />
              {{ $t("view") }}
            </b-button>

            <!-- Si besoin : bouton de suppression -->
            <delete-button
              v-if="can('delete imports')"
              @success="deleteItem(data.item)"
              class="ml-2 actions-danger"
              type="import"
              :title="`Delete import #${data.item.id}`"
            />
          </div>
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination.lastPage > 1"
        v-model="pagination.currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.perPage"
        aria-controls="import-orders-table"
      ></b-pagination>
    </b-card-body>

    <!-- FOOTER : affichage d’un petit résumé du nombre de résultats -->
    <b-card-footer>
      <div class="text-center" v-if="pagination.total > 0">
        {{
          $t("showing_x_results", {
            current: (pagination.to - pagination.from + 1) || 0,
            total: pagination.total
          })
        }}
        <span v-if="hasFilter">
          · {{ $t("results_filtered") }} ·
          <b-link @click="resetFilters()">{{ $t("clear_all") }}</b-link>
        </span>
      </div>
    </b-card-footer>

    <!-- MODAL d’Upload du fichier -->
    <b-modal
      id="modal-upload"
      size="md"
      hide-footer
      :title="$t('upload_orders_file')"
      v-model="showUploadModal"
    >
      <b-form @submit.prevent="uploadFile">
        <b-form-file
          v-model="file"
          :state="fileState"
          placeholder="Choose a file..."
          drop-placeholder="Drop file here..."
          required
        />
        <div class="mt-3 text-right">
          <b-button variant="secondary" @click="showUploadModal = false">
            {{ $t("cancel") }}
          </b-button>
          <b-button type="submit" variant="primary">
            {{ $t("submit") }}
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- MODAL d’erreur -->
    <b-modal
      id="modal-error"
      size="sm"
      title="Error"
      hide-footer
      v-model="showErrorModal"
    >
      <div class="d-flex justify-content-between flex-column">
        <h6>{{ errorMessage }}</h6>
      </div>
    </b-modal>

    <!-- MODAL de succès -->
    <b-modal
      id="modal-success"
      size="sm"
      title="Success"
      hide-footer
      v-model="showSuccessModal"
    >
      <div class="d-flex justify-content-between flex-column">
        <h6>{{ successMessage }}</h6>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import axios from "axios";
import deleteButton from "../Common/DeleteButton";
import moment from "moment";

export default {
  name: "ImportOrderList",
  components: { deleteButton },
  props: {
    permissions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      ready: false, // Contrôle l’affichage du b-card
      showFilters: false, // Toggle pour les filtres avancés
      showUploadModal: false,
      showErrorModal: false,
      showSuccessModal: false,

      // Erreur / succès
      errorMessage: "",
      successMessage: "",

      // Fichier sélectionné
      file: null,

      // Filtres de recherche
      filters: {
        query: null,
        hasErrors: null,
        isProcessed: null
      },

      // Options pour les filtres
      hasErrorsOptions: [
        { value: null, text: this.$t("all") },
        { value: "yes", text: this.$t("has_errors") + " : Yes" },
        { value: "no", text: this.$t("has_errors") + " : No" }
      ],
      isProcessedOptions: [
        { value: null, text: this.$t("all") },
        { value: "yes", text: "Processed" },
        { value: "no", text: "Not processed" }
      ],

      // Champs du tableau
      fields: [
        { key: "id", label: this.$t("ID"), class: "text-nowrap align-middle" },
        {
          key: "path",
          label: this.$t("Path"),
          class: "text-nowrap align-middle"
        },
        {
          key: "errors",
          label: this.$t("Has Errors"),
          class: "text-nowrap align-middle",
          sortable: false
        },
        {
          key: "is_processed",
          label: this.$t("Is Processed"),
          class: "text-nowrap align-middle",
          sortable: false
        },
        {
          key: "created_at",
          label: this.$t("Import date"),
          class: "text-nowrap align-middle",
          sortable: false
        },
        {
          key: "actions",
          class: "align-right",
          sortable: false
        }
      ],

      // Données de la table
      importOrders: this.getItems,

      // Gestion du chargement et de la pagination
      loading: false,
      pagination: {
        total: 0,
        perPage: 15,
        currentPage: 1,
        lastPage: 1,
        from: 0,
        to: 0
      }
    };
  },
  computed: {
    fileState() {
      return this.file ? true : null;
    },
    hasFilter() {
      return (
        this.filters.query || this.filters.hasErrors || this.filters.isProcessed
      );
    }
  },
  mounted() {
    this.ready = true;
    this.fetchImportOrders();
  },
  methods: {
    can(permissionName) {
      return this.permissions.some(
        permission => permission.name === permissionName
      );
    },
    getItems(ctx, callback) {
      this.pagination.currentPage = ctx.currentPage;
      this.fetchImportOrders().then(() => {
        callback(this.importOrders, this.pagination.total);
      });

      return null; // b-table s’attend à un retour synchrone
    },
    fetchImportOrders() {
      this.loading = true;
      const params = {
        page: this.pagination.currentPage,
        per_page: this.pagination.perPage,
        query: this.filters.query,
        has_errors: this.filters.hasErrors,
        is_processed: this.filters.isProcessed
      };

      return axios
        .get("/api/orders/imports", { params })
        .then(response => {
          const data = response.data;
          this.importOrders = data.data;
          this.pagination = {
            ...this.pagination,
            total: data.meta.total,
            perPage: data.meta.per_page,
            currentPage: data.meta.current_page,
            lastPage: data.meta.last_page,
            from: data.meta.from,
            to: data.meta.to
          };
        })
        .catch(error => {
          this.errorMessage = error?.response?.data?.message || "Error";
          this.showErrorModal = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    uploadFile() {
      if (!this.file) {
        return;
      }
      this.loading = true;

      const formData = new FormData();
      formData.append("file", this.file);

      axios
        .post("/api/orders/upload", formData)
        .then(() => {
          this.successMessage = this.$t("file_uploaded_successfully");
          this.showSuccessModal = true;
          // On recharge la liste
          this.fetchImportOrders();
        })
        .catch(error => {
          this.errorMessage =
            error?.response?.data?.message || this.$t("error_uploading_file");
          this.showErrorModal = true;
        })
        .finally(() => {
          this.loading = false;
          this.file = null;
          this.showUploadModal = false;
        });
    },
    deleteItem(item) {
      axios
        .delete(`/api/imports/${item.id}`)
        .then(() => {
          this.successMessage = this.$t("import_deleted_successfully");
          this.showSuccessModal = true;
          this.fetchImportOrders();
        })
        .catch(error => {
          this.errorMessage =
            error?.response?.data?.message || this.$t("unknown_error");
          this.showErrorModal = true;
        });
    },
    formatDate(date) {
      if (!date) return "";
      0;
      return moment(date).format("DD-MM-YYYY HH:mm");
    },
    viewRoute(id) {
      return `/imports/show/${id}`;
    },
    resetFilters() {
      this.filters.query = null;
      this.filters.hasErrors = null;
      this.filters.isProcessed = null;
      this.$nextTick(() =>
        this.$root.$emit("bv::refresh::table", "import-orders-table")
      );
    }
  }
};
</script>

<style scoped>
.card-container {
  margin-bottom: 2rem;
}

.reports-filters {
  border-radius: 4px;
}

/* Si tu veux un overlay loader, tu peux le réactiver si besoin */
.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
</style>
